import React from "react";
import { Empty } from "antd/lib";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  ResponsiveContainer,
  LabelList,
  CartesianGrid,
  Cell,
} from "recharts";
import { ITinyBarChart } from "../../interfaces/dashboard";
import { COLORS } from "../../constants/dashboard";

const formatterUS = new Intl.NumberFormat("en-US");

interface TinyBarChartProps {
  data: ITinyBarChart[];
  color?: string;
  height?: number;
  loading?: boolean;
}

const LABEL_FONT_SIZE = 14;
const LABEL_FONT_WEIGHT = 500;
const LABEL_PADDING = 6;
const LABEL_MIN_WIDTH = 24; // Minimum width to keep label inside
const CHAR_WIDTH_ESTIMATE = 8; // Estimate width per character for label

const TinyBarChart: React.FC<TinyBarChartProps> = ({
  data = [],
  color,
  height = 350,
  loading = false,
}) => {
  if (data.length === 0 && !loading) {
    return <Empty description="No Data" style={{ marginTop: "30px" }} />;
  }

  if (data.length > 0) {
    // Find max value for X axis domain
    const maxValue = Math.max(...data.map((d) => d.value));

    return (
      <ResponsiveContainer width="100%" height={height}>
        <BarChart
          layout="vertical"
          data={data}
          margin={{ top: 0, right: 10, left: 2, bottom: 0 }}
        >
          <CartesianGrid strokeDasharray="3 3" />

          <XAxis
            type="number"
            orientation="top"
            tick={{ fontSize: 14 }}
            tickFormatter={(value) => formatterUS.format(value)}
            domain={[0, maxValue]}
          />

          <YAxis
            dataKey="label"
            type="category"
            width={175}
            tick={{ fontSize: 12 }}
          />

          <Bar dataKey="value" fill={color}>
            {!color &&
              data.map((_, index) => (
                <Cell
                  key={`cell-${index}`}
                  fill={COLORS[index % COLORS.length]}
                />
              ))}

            <LabelList
              dataKey="value"
              content={(props: any) => {
                const {
                  x = 0,
                  y = 0,
                  width = 0,
                  height: labelH = 0,
                  value,
                  index,
                } = props;

                const percent = data[index]?.percent;
                const valueText = formatterUS.format(value);
                const percentText =
                  percent !== undefined ? `(${percent}%)` : "";

                // Estimate the maximum text width (for value or percent)
                const textWidth =
                  Math.max(valueText.length, percentText.length) *
                    CHAR_WIDTH_ESTIMATE +
                  8;

                let textX: number;
                let textAnchor: "middle" | "start" | "end";
                let textY: number;

                if (!percentText) {
                  // No percent: if value is 0 or bar is too small, show label outside right
                  if (value === 0 || width < LABEL_MIN_WIDTH) {
                    textX = x + width + LABEL_PADDING;
                    textAnchor = "start";
                  } else {
                    textX = x + width - LABEL_PADDING;
                    textAnchor = "end";
                  }
                  // Vertically center for single line
                  textY = y + labelH / 2 + 5;
                } else {
                  // With percent: decide if label should be inside or outside the bar
                  const renderInside =
                    width > textWidth + LABEL_PADDING * 2;
                  textX = renderInside
                    ? x + width / 2
                    : x + width + LABEL_PADDING;
                  textAnchor = renderInside ? "middle" : "start";
                  // Center for two lines
                  textY = y + labelH / 2 - 4;
                }

                return (
                  <text
                    x={textX}
                    y={textY}
                    fill="#002233"
                    textAnchor={textAnchor}
                    fontSize={LABEL_FONT_SIZE}
                    fontWeight={LABEL_FONT_WEIGHT}
                  >
                    {/* Value on the first line */}
                    <tspan x={textX} dy={0}>
                      {valueText}
                    </tspan>
                    {/* Percent on the second line, if available */}
                    {percentText && (
                      <tspan x={textX} dy={18}>
                        {percentText}
                      </tspan>
                    )}
                  </text>
                );
              }}
            />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    );
  }

  return null;
};

export default React.memo(TinyBarChart);
